import React, { useState } from "react";
import { createPortal } from "react-dom";
import type { StudentSearchResult } from "../../../../../interfaces/classDetail";
import { useHttpClient } from "../../../../../hooks/useHttpClient";
import { useLocalization } from "../../../../../stores/localizationContext";
import { useUserContext } from "../../../../../stores/userContext";
import "./AddStudentPopup.scss";

interface AddStudentPopupProps {
  classId: string;
  onCancel: () => void;
  onStudentAdded: () => void;
}

const ERROR_MESSAGES: Record<number, string> = {
  4041: "student_not_found_error",
  4006: "class_full_capacity_error",
  40034: "class_not_eligible_error",
  40035: "student_already_enrolled_error",
  40036: "student_already_has_access_error",
  40310: "educator_not_assigned_error",
  40912: "grade_change_pending_error",
  42220: "invalid_block_error",
};

const ERROR_FALLBACKS: Record<number, string> = {
  4041: "This account doesn't exist.",
  4006: "This class has reached full capacity, and no seats are currently open.",
  40034: "Class not eligible for enrollment.",
  40035: "This student is already enrolled in a similar class.",
  40036: "This student already has access in the specified class or block.",
  40310: "Unauthorized access. Educator is not assigned to this class.",
  40912: "The grade change process cannot be initiated until all wallet transactions have been settled.",
  42220: "Cannot pay the block. One or more block(s) do not exist in the class.",
};

const AddStudentPopup: React.FC<AddStudentPopupProps> = ({
  classId,
  onCancel,
  onStudentAdded,
}) => {
  const { t } = useLocalization();
  const { user } = useUserContext();
  const api = useHttpClient();

  const [mobileNumber, setMobileNumber] = useState("");
  const [searchResult, setSearchResult] = useState<StudentSearchResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getErrorMessage = (errorId: number, fallbackMessage?: string): string => {
    const key = ERROR_MESSAGES[errorId];
    const fallback = ERROR_FALLBACKS[errorId] ?? fallbackMessage ?? t("generic_error", "Something went wrong. Please try again later.");
    return key ? t(key, fallback) : fallback;
  };

  const handleSearch = async () => {
    if (!mobileNumber.trim() || !user?.userId) return;

    setSearchLoading(true);
    setError(null);
    setSearchResult(null);

    try {
      const endpoint = import.meta.env.VITE_STUDENT_SEARCH.replace(
        "{educator_id}",
        user.userId
      );
      const response = await api.post(endpoint, {
        mobile_number: `+2${mobileNumber.trim()}`,
      });
      setSearchResult(response.data);
    } catch (err: any) {
      const errorId = err?.response?.data?.error_id;
      const serverMessage = err?.response?.data?.error_message;
      if (errorId) {
        setError(getErrorMessage(errorId, serverMessage));
      } else {
        setError(t("generic_error", "Something went wrong. Please try again later."));
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!searchResult || !user?.userId) return;

    setAddLoading(true);
    setError(null);

    try {
      const endpoint = import.meta.env.VITE_ADD_STUDENT_AFFILIATION
        .replace("{educator_id}", user.userId)
        .replace("{class_id}", classId);

      await api.post(endpoint, {
        student_id: searchResult.student_id,
      });

      onStudentAdded();
    } catch (err: any) {
      const errorId = err?.response?.data?.error_id;
      const serverMessage = err?.response?.data?.error_message;
      if (errorId) {
        setError(getErrorMessage(errorId, serverMessage));
      } else {
        setError(t("generic_error", "Something went wrong. Please try again later."));
      }
    } finally {
      setAddLoading(false);
    }
  };

  const isSearchEnabled = mobileNumber.replace(/\s/g, "").length === 11 && !searchLoading && !addLoading;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isSearchEnabled) {
      handleSearch();
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="add-student-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="add-student-popup__header">
          <button
            className="add-student-popup__cancel-btn"
            onClick={onCancel}
            disabled={addLoading}
          >
            {t("cancel_label")}
          </button>
          <h2 className="add-student-popup__title">
            {t("add_affiliate_student_button")}
          </h2>
          <button
            className="add-student-popup__add-btn"
            onClick={handleAdd}
            disabled={!searchResult || addLoading}
          >
            {addLoading ? t("adding", "Adding...") : t("add_label")}
          </button>
        </div>

        {/* Input — grey section */}
        <div className="add-student-popup__input-section">
          <div className="add-student-popup__input-wrapper">
            <span className="add-student-popup__flag">
              <img
                src="https://flagcdn.com/w40/eg.png"
                alt="Egypt"
                className="add-student-popup__flag-img"
              />
            </span>
            <span className="add-student-popup__prefix">+20</span>
            <input
              className="add-student-popup__input"
              type="tel"
              placeholder={t("student_mobile_number_label")}
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={searchLoading || addLoading}
            />
          </div>
        </div>

        {/* Search button — right-aligned, outside grey box */}
        <div className="add-student-popup__search-row">
          <button
            className="add-student-popup__search-btn"
            onClick={handleSearch}
            disabled={!isSearchEnabled}
          >
            {searchLoading ? t("searching", "...") : t("search_label")}
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="add-student-popup__error-banner">
            {error}
          </div>
        )}

        {/* Student result card */}
        {searchResult && (
          <div className="add-student-popup__student-card">
            <div className="add-student-popup__student-avatar">
              <span className="add-student-popup__avatar-initials">
                {searchResult.first_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="add-student-popup__student-info">
              <span className="add-student-popup__student-name">
                {searchResult.first_name} {searchResult.last_name}
              </span>
              <span className="add-student-popup__student-grade">
                {t(searchResult.grade_localization_key, searchResult.grade_localization_key)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AddStudentPopup;
